import { useState, useEffect, useCallback } from 'react';
import { api, MarketplaceEntity, MarketplaceEntitySearchResult, EntityType } from '../../lib/api';
import { traceStart, traceEnd, traceError, addBreadcrumb } from '../../lib/telemetry';

interface UseMarketplaceEntitySearchOptions {
  query?: string;
  categoryId?: string;
  type?: EntityType;
  sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
  limit?: number;
}

interface UseMarketplaceEntitySearchReturn {
  results: MarketplaceEntitySearchResult | null;
  loading: boolean;
  error: string | null;
  entities: MarketplaceEntity[];
  total: number;
  hasMore: boolean;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  refresh: () => void;
}

export function useMarketplaceEntitySearch(
  options: UseMarketplaceEntitySearchOptions = {}
): UseMarketplaceEntitySearchReturn {
  const { query, categoryId, type, sortBy, limit = 20 } = options;

  const [results, setResults] = useState<MarketplaceEntitySearchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const offset = (page - 1) * limit;

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);

    const searchParams = { q: query, categoryId, type, sortBy, limit, offset };
    const traceId = traceStart('marketplaceEntitySearch', {
      searchParams,
      apiBase: import.meta.env.VITE_API_URL || '/v1',
    });

    addBreadcrumb('marketplace', 'Starting entity search', searchParams);

    try {
      const result = await api.searchMarketplaceEntities(searchParams);

      addBreadcrumb('marketplace', `Entity search returned ${result.entities.length} entities`, {
        total: result.total,
        hasMore: result.hasMore,
        entityIds: result.entities.slice(0, 5).map(e => e.entityId),
      });

      traceEnd(traceId, {
        entityCount: result.entities.length,
        total: result.total,
        hasMore: result.hasMore,
      });

      setResults(result);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to search entities';
      traceError(traceId, err instanceof Error ? err : new Error(String(err)), {
        searchParams,
      });
      addBreadcrumb('marketplace', `Entity search failed: ${errorMsg}`);
      setError(errorMsg);
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, [query, categoryId, type, sortBy, limit, offset]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  // Reset to page 1 when search parameters change
  useEffect(() => {
    setPage(1);
  }, [query, categoryId, type, sortBy]);

  const entities = results?.entities ?? [];
  const total = results?.total ?? 0;
  const hasMore = results?.hasMore ?? false;
  const totalPages = Math.ceil(total / limit);

  return {
    results,
    loading,
    error,
    entities,
    total,
    hasMore,
    page,
    setPage,
    totalPages,
    refresh: fetchResults,
  };
}
